/**
 * customers-shared — the pieces the list, the detail and the Columns panel all
 * need: the tenant noun, column labels, one cell renderer, and the lifecycle
 * chip. Kept together so the three surfaces cannot drift into three different
 * answers for "what is this column called" or "what does a missing value look
 * like".
 */
import type { CSSProperties, ReactNode } from "react";
import { Avatar, Icon, T } from "@v0y4ge/jrni-ui";
import { useT, formatICU } from "../../lib/i18n";
import { friendlyDateShort } from "../../lib/datetime";
import { EM_DASH, DEFAULT_COLUMN_LABELS, attributeKey, columnSource, customerColumnHeaderKey, humaniseFieldKey, projectCell } from "./customer-columns";
import type { CustomerDirectoryRecord, CustomerListField } from "./customers-data";
import type { TypeCatalog } from "./customer-types";
import { TypeBadge } from "./CustomerTypeCard";

/** Up to two initials from a display name. Titles are dropped; a single-word
 *  name contributes one letter rather than a fabricated second. */
export function initialsOf(name: string): string {
  return String(name || "")
    .replace(/^(Mme\.|Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s*/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** The tenant's noun for one record and for many. Server-merged catalog only —
 *  never an industry map, a tenant-slug conditional or the vocabulary blob. */
export function useCustomerNoun(): { noun: string; nounPlural: string } {
  const t = useT();
  return { noun: t("common.customer", "Customer"), nounPlural: t("common.customers", "Customers") };
}

/** Interpolate a message that also needs a call-site English fallback. */
export function useIcu(): (key: string, fallback: string, params: Record<string, string | number>) => string {
  const t = useT();
  return (key, fallback, params) => formatICU(t(key, fallback), params);
}

/**
 * Column header text. Order is fixed: translation key → authoritative catalog
 * label → humanised field key. A resolution that would show the raw key is
 * treated as no translation at all.
 */
export function useColumnLabel(catalog?: ReadonlyMap<string, CustomerListField>) {
  const t = useT();
  const { noun } = useCustomerNoun();
  return (id: string): string => {
    if (id === "name") return formatICU(t("customers.columns.name", "{noun}"), { noun });
    const key = customerColumnHeaderKey(id);
    const fallback = DEFAULT_COLUMN_LABELS[id];
    // First-class ids carry the agreed English wording as their call-site
    // fallback; dynamic ids have none, so a missing key falls through to the
    // catalog's own label before anything is humanised.
    const translated = t(key, fallback ?? "");
    if (translated && translated !== key) return translated;
    const field = catalog?.get(id);
    if (field?.labelKey) {
      const viaField = t(field.labelKey, "");
      if (viaField && viaField !== field.labelKey) return viaField;
    }
    if (field?.label) return field.label;
    return humaniseFieldKey(attributeKey(id) ?? id);
  };
}

/** Source badge for a dynamic field — "SIS" / "Custom"; standard fields none. */
export function SourceBadge({ id }: { id: string }) {
  const t = useT();
  const source = columnSource(id);
  if (source === "standard") return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "1px 6px", borderRadius: 4,
      background: T.n6, color: T.n2, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
    }}>
      {source === "sis" ? t("customers.columns.sourceSis", "SIS") : t("customers.columns.sourceCustom", "Custom")}
    </span>
  );
}

const mono: CSSProperties = { fontFamily: T.fontMono, fontSize: 12.5, color: T.n2 };

/** One table cell. `null` renders an em dash; `0` renders 0. */
export function CustomerCell({ row, columnId, catalog, externalIdLabel }: {
  row: CustomerDirectoryRecord;
  columnId: string;
  catalog: TypeCatalog;
  externalIdLabel: string | null;
}) {
  const t = useT();
  const spec = projectCell(row, columnId);
  const dash = <span style={{ color: T.soft }}>{EM_DASH}</span>;

  if (spec.kind === "name") {
    const name = typeof spec.value === "string" && spec.value ? spec.value : EM_DASH;
    const ext = row.externalId?.value ?? null;
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
        <Avatar size={32} initials={initialsOf(name)} />
        <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
          {ext ? (
            <span style={{ ...mono, fontSize: 11, color: T.soft }}>
              {externalIdLabel ? `${externalIdLabel} ${ext}` : ext}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
  if (spec.kind === "type") {
    return <TypeBadge catalog={catalog} id={typeof spec.value === "string" ? spec.value : null} />;
  }
  if (spec.value === null) return dash;
  if (spec.kind === "count") return <span style={{ ...mono, fontVariantNumeric: "tabular-nums" }}>{spec.value}</span>;
  if (spec.kind === "date") return <span style={{ fontSize: 12.5, color: T.n2 }}>{friendlyDateShort(String(spec.value))}</span>;
  if (spec.kind === "boolean") {
    return (
      <span style={{ fontSize: 12.5, color: T.n2 }}>
        {spec.value ? t("customers.consent.marketing.granted", "Granted") : t("customers.consent.marketing.withheld", "Not given")}
      </span>
    );
  }
  if (spec.kind === "identifier") return <span style={{ ...mono, fontSize: 12 }}>{spec.value}</span>;
  return <span style={{ fontSize: 12.5, color: T.n2, overflow: "hidden", textOverflow: "ellipsis" }}>{spec.value}</span>;
}

/** Lifecycle chip for a service-record entry. Seven declared states; an
 *  undeclared one renders its own value neutrally rather than crashing. */
export function LifecycleChip({ state }: { state: string }) {
  const t = useT();
  const TONE: Record<string, { bg: string; fg: string }> = {
    requested: { bg: T.n6, fg: T.n2 },
    waitlisted: { bg: T.orange2, fg: T.orange1 },
    confirmed: { bg: `color-mix(in srgb, ${T.tDarkBlue} 10%, ${T.white})`, fg: T.tDarkBlue },
    inProgress: { bg: `color-mix(in srgb, ${T.tDarkBlue} 10%, ${T.white})`, fg: T.tDarkBlue },
    completed: { bg: `color-mix(in srgb, ${T.green1} 12%, ${T.white})`, fg: T.green1 },
    cancelled: { bg: T.n6, fg: T.soft },
    noShow: { bg: `color-mix(in srgb, ${T.red1} 10%, ${T.white})`, fg: T.red1 },
  };
  const tone = TONE[state] ?? { bg: T.n6, fg: T.n2 };
  const label = t(`customers.lifecycle.${state}`, humaniseFieldKey(state.replace(/([A-Z])/g, " $1").toLowerCase()));
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 9px", borderRadius: 999,
      background: tone.bg, color: tone.fg, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

/** A filter-independent directory total. */
export function Kpi({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "13px 16px", minWidth: 0 }}>
      <span style={{ fontSize: 11, color: T.soft, fontWeight: 600, display: "block", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ fontFamily: T.fontMono, fontSize: 21, fontWeight: 700, lineHeight: 1.05, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

/** Section shell used by every detail section. */
export function DetailSection({ title, action, children }: { title: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${T.n6}` }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: T.n1, margin: 0 }}>{title}</h2>
        {action ? <span style={{ marginLeft: "auto" }}>{action}</span> : null}
      </div>
      {children}
    </section>
  );
}

/** Label + value pair, em dash when there is nothing. */
export function Facts({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "minmax(140px, 220px) 1fr", gap: "10px 16px", margin: 0, padding: "14px 16px" }}>
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`} style={{ display: "contents" }}>
          <dt style={{ fontSize: 11.5, color: T.soft, fontWeight: 600 }}>{r.label}</dt>
          <dd style={{ fontSize: 13, color: T.n1, margin: 0, minWidth: 0, overflowWrap: "anywhere" }}>
            {r.value === null || r.value === undefined || r.value === "" ? <span style={{ color: T.soft }}>{EM_DASH}</span> : r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Inline retryable failure. A read that failed is never an empty section. */
export function ReadError({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel: string }) {
  return (
    <div role="alert" style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 16px", fontSize: 12.5, color: T.n1 }}>
      <span style={{ display: "inline-flex", color: T.orange1 }}><Icon.warn size={14} /></span>
      <span style={{ flex: 1 }}>{message}</span>
      {onRetry ? (
        <button type="button" onClick={onRetry} style={{
          border: `1px solid ${T.border}`, background: T.white, borderRadius: 6, padding: "5px 10px",
          fontSize: 12, fontWeight: 600, color: T.n1, cursor: "pointer", fontFamily: T.font,
        }}>{retryLabel}</button>
      ) : null}
    </div>
  );
}
